import {
    Children,
    cloneElement,
    isValidElement,
    type MouseEventHandler,
    type ReactElement,
    type Ref,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LuEllipsisVertical } from 'react-icons/lu';

import {
    CONTEXT_MENU_DEFAULT_PANEL_WIDTH,
    CONTEXT_MENU_DIRECTION,
    CONTEXT_MENU_MAX_HEIGHT,
    type ContextMenuItem,
    ContextMenuItemVariant,
    type ContextMenuProps,
} from './types';
import { COMPONENTS_NAMESPACE } from '../../locales';
import { cn } from '../../utils/cn';
import { handleEventBehavior } from '../../utils/EventBehavior';

export type { ContextMenuItem };

/**
 * A context menu component for displaying action items.
 * Supports both click-triggered (via trigger prop) and right-click triggered (via children prop) modes.
 *
 * @param items - Array of menu items to display.
 * @param trigger - Custom trigger element. If not provided, uses an ellipsis icon button. Not rendered when children is provided.
 * @param children - Content that triggers the menu on right-click. Menu opens at cursor position.
 * @param triggerClassName - Additional CSS classes for trigger.
 * @param open - Controlled open state.
 * @param onOpenChange - Callback when open state changes.
 * @param panelClassName - Additional CSS classes for panel.
 * @param panelWidth - Minimum width of the panel in pixels. Defaults to 200.
 * @param maxHeight - Maximum height of the panel in pixels. Defaults to CONTEXT_MENU_MAX_HEIGHT (149).
 * @param triggerAriaLabel - Accessible label for the trigger button.
 * @param className - Additional CSS classes.
 *
 * @example
 * // Click trigger (default ellipsis button)
 * <ContextMenu
 *   items={[
 *     { id: '1', label: 'Edit', icon: <LuPencil />, onClick: () => console.log('Edit') },
 *     { id: '2', label: 'Delete', icon: <LuTrash2 />, onClick: () => console.log('Delete'), variant: 'destructive' },
 *   ]}
 * />
 *
 * @example
 * // Custom click trigger
 * <ContextMenu
 *   items={menuItems}
 *   trigger={<Button variant="secondary">Actions</Button>}
 * />
 *
 * @example
 * // Right-click trigger
 * <ContextMenu items={menuItems}>
 *   <div className="p-4 border">Right-click me</div>
 * </ContextMenu>
 */
export const ContextMenu: React.FC<ContextMenuProps> = ({
    children,
    className,
    eventPreventDefault,
    eventStopPropagation,
    items,
    maxHeight = CONTEXT_MENU_MAX_HEIGHT,
    onOpenChange,
    open: controlledOpen,
    panelClassName,
    panelWidth: panelWidthProp = CONTEXT_MENU_DEFAULT_PANEL_WIDTH,
    trigger,
    triggerAriaLabel,
    triggerClassName,
    ...props
}) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);
    const resolvedTriggerAriaLabel = triggerAriaLabel ?? t('contextMenu.triggerAriaLabel');
    const [internalOpen, setInternalOpen] = useState(false);
    const [panelPosition, setPanelPosition] = useState<{ width: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const isContextMenuTriggerRef = useRef(false);
    const hasAdjustedPositionRef = useRef(false);

    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;

    const handleOpenChange = useCallback(
        (newOpen: boolean) => {
            if (!isControlled) {
                setInternalOpen(newOpen);
            }
            onOpenChange?.(newOpen);
        },
        [isControlled, onOpenChange],
    );

    const calculatePosition = useCallback(() => {
        const targetElement = triggerRef.current || containerRef.current;
        if (!targetElement) {
            return;
        }

        const rect = targetElement.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const panelHeight = panelRef.current?.offsetHeight ?? maxHeight;
        const panelWidth = Math.max(panelWidthProp, rect.width);

        const newPosition =
            spaceBelow < panelHeight && spaceAbove > spaceBelow
                ? CONTEXT_MENU_DIRECTION.TOP
                : CONTEXT_MENU_DIRECTION.BOTTOM;

        let x = rect.left;
        const estimatedHeight = panelRef.current?.offsetHeight ?? maxHeight;
        let y = newPosition === CONTEXT_MENU_DIRECTION.BOTTOM ? rect.bottom + 2.6 : rect.top - estimatedHeight - 2.6;

        // Adjust if menu would overflow right edge
        if (x + panelWidth > window.innerWidth) {
            x = window.innerWidth - panelWidth - 8;
        }

        // Adjust if menu would overflow left edge
        if (x < 8) {
            x = 8;
        }

        // Adjust if menu would overflow top edge
        if (y < 8) {
            y = 8;
        }

        // Adjust if menu would overflow bottom edge
        if (y + panelHeight > window.innerHeight) {
            y = window.innerHeight - panelHeight - 8;
        }

        setPanelPosition({ width: panelWidth, x, y });
    }, [maxHeight, panelWidthProp]);

    const handleToggle = useCallback(
        (e: React.MouseEvent) => {
            handleEventBehavior(e, { eventPreventDefault, eventStopPropagation });
            const willOpen = !isOpen;

            if (willOpen) {
                isContextMenuTriggerRef.current = false;
                hasAdjustedPositionRef.current = false;
                const targetElement = triggerRef.current || containerRef.current;
                if (targetElement) {
                    const rect = targetElement.getBoundingClientRect();
                    const width = Math.max(panelWidthProp, rect.width);
                    setPanelPosition({ width, x: rect.left, y: rect.bottom + 2.6 });
                }
            }

            handleOpenChange(willOpen);
        },
        [eventPreventDefault, eventStopPropagation, isOpen, handleOpenChange, panelWidthProp],
    );

    const handleContextMenu = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault();

            isContextMenuTriggerRef.current = true;

            const panelWidth = panelWidthProp;
            const panelHeight = panelRef.current?.offsetHeight ?? maxHeight;

            // Calculate position based on cursor, adjusting for viewport edges
            let x = event.clientX;
            let y = event.clientY;

            // Adjust if menu would overflow right edge
            if (x + panelWidth > window.innerWidth) {
                x = window.innerWidth - panelWidth - 8;
            }

            // Adjust if menu would overflow left edge
            if (x < 8) {
                x = 8;
            }

            // Adjust if menu would overflow bottom edge
            if (y + panelHeight > window.innerHeight) {
                y = window.innerHeight - panelHeight - 8;
            }

            // Adjust if menu would overflow top edge
            if (y < 8) {
                y = 8;
            }

            setPanelPosition({ width: panelWidth, x, y });
            handleOpenChange(true);
        },
        [handleOpenChange, maxHeight, panelWidthProp],
    );

    const handleItemClick = useCallback(
        (item: ContextMenuItem) => {
            if (item.disabled) {
                return;
            }
            item.onClick?.();
            handleOpenChange(false);
        },
        [handleOpenChange],
    );

    const handlePanelKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            const menuItems = Array.from(
                panelRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [],
            );
            const currentIndex = menuItems.indexOf(document.activeElement as HTMLButtonElement);
            let nextIndex: number | null = null;

            switch (event.key) {
                case 'ArrowDown':
                    nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
                    break;
                case 'ArrowUp':
                    nextIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
                    break;
                case 'End':
                    nextIndex = menuItems.length - 1;
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'Tab':
                    event.preventDefault();
                    handleOpenChange(false);
                    triggerRef.current?.focus();
                    return;
                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    handleOpenChange(false);
                    triggerRef.current?.focus();
                    return;
            }

            if (nextIndex !== null && menuItems[nextIndex]) {
                event.preventDefault();
                menuItems[nextIndex].focus();
            }
        },
        [handleOpenChange],
    );

    useLayoutEffect(() => {
        if (
            isOpen &&
            panelPosition &&
            !isContextMenuTriggerRef.current &&
            !hasAdjustedPositionRef.current &&
            panelRef.current
        ) {
            hasAdjustedPositionRef.current = true;
            calculatePosition();
        }
    }, [isOpen, panelPosition, calculatePosition]);

    // Handle controlled mode: set initial position when opened externally
    useEffect(() => {
        if (isOpen && !panelPosition) {
            const targetElement = triggerRef.current || containerRef.current;
            if (targetElement) {
                const rect = targetElement.getBoundingClientRect();
                const width = Math.max(panelWidthProp, rect.width);
                setPanelPosition({ width, x: rect.left, y: rect.bottom + 2.6 });
            } else {
                setPanelPosition({ width: panelWidthProp, x: 8, y: 8 });
            }
        }
    }, [isOpen, panelPosition, panelWidthProp]);

    useEffect(() => {
        if (!isOpen || !panelPosition) return undefined;

        const animationFrame = requestAnimationFrame(() => {
            panelRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
        });

        return () => cancelAnimationFrame(animationFrame);
    }, [isOpen, panelPosition]);

    useEffect(() => {
        if (!isOpen) {
            setPanelPosition(null);
            hasAdjustedPositionRef.current = false;
            return undefined;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            // For single element children mode, containerRef is not set, so just check panel
            const isOutsideContainer = !containerRef.current || !containerRef.current.contains(target);
            const isOutsidePanel = !panelRef.current || !panelRef.current.contains(target);

            if (isOutsideContainer && isOutsidePanel) {
                handleOpenChange(false);
            }
        };

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleOpenChange(false);
            }
        };

        const handlePosition = () => {
            if (isContextMenuTriggerRef.current) {
                handleOpenChange(false);
            } else {
                calculatePosition();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);
        window.addEventListener('resize', handlePosition);
        window.addEventListener('scroll', handlePosition, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
            window.removeEventListener('resize', handlePosition);
            window.removeEventListener('scroll', handlePosition, true);
        };
    }, [isOpen, handleOpenChange, calculatePosition]);

    const renderTrigger = () => {
        // Right-click mode with multiple children: wrap in a div
        // (single element children are handled separately in the return statement)
        if (children) {
            return <div onContextMenu={handleContextMenu}>{children}</div>;
        }

        // Custom trigger mode
        if (isValidElement(trigger)) {
            type CustomTriggerProps = {
                'aria-expanded'?: boolean;
                'aria-haspopup'?: 'menu';
                onClick?: MouseEventHandler<HTMLButtonElement>;
                ref?: Ref<HTMLButtonElement>;
            };
            const customTrigger = trigger as ReactElement<CustomTriggerProps>;
            const customTriggerRef = customTrigger.props.ref;
            const mergedRef = (node: HTMLButtonElement | null) => {
                triggerRef.current = node;
                if (typeof customTriggerRef === 'function') {
                    customTriggerRef(node);
                } else if (customTriggerRef) {
                    customTriggerRef.current = node;
                }
            };

            return cloneElement(customTrigger, {
                'aria-expanded': isOpen,
                'aria-haspopup': 'menu',
                onClick: (event) => {
                    customTrigger.props.onClick?.(event);
                    if (!event.defaultPrevented) handleToggle(event);
                },
                ref: mergedRef,
            });
        }

        // Default ellipsis button
        return (
            <button
                ref={triggerRef}
                type="button"
                onClick={handleToggle}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                aria-label={resolvedTriggerAriaLabel}
                className={cn(
                    'flex items-center justify-center',
                    'p-1.5',
                    'rounded',
                    'text-midnight',
                    'hover:bg-surface-light',
                    'focus-visible:outline-2 focus-visible:outline-blue-focus',
                    'transition-colors',
                    triggerClassName,
                )}
            >
                <LuEllipsisVertical className="size-5" />
            </button>
        );
    };

    const renderPanel = () =>
        isOpen &&
        panelPosition &&
        createPortal(
            <div
                ref={panelRef}
                role="menu"
                onKeyDown={handlePanelKeyDown}
                className={cn(
                    'fixed z-[9999]',
                    'bg-white',
                    'border border-grey-outline-light border-solid',
                    'rounded-md',
                    'shadow-[0px_2px_6px_0px_rgba(163,177,196,0.2)]',
                    'py-1',
                    'overflow-hidden',
                    panelClassName,
                )}
                style={{
                    contain: 'layout style paint',
                    isolation: 'isolate',
                    left: `${panelPosition.x}px`,
                    maxHeight: `${maxHeight}px`,
                    minWidth: `${panelPosition.width}px`,
                    top: `${panelPosition.y}px`,
                }}
            >
                <div className="flex flex-col overflow-y-auto" style={{ maxHeight: `${maxHeight}px` }}>
                    {items.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            disabled={item.disabled}
                            onClick={() => handleItemClick(item)}
                            className={cn(
                                'flex items-center gap-2',
                                'h-8 px-2 mx-1',
                                'rounded-sm',
                                'font-normal font-roboto text-sm leading-[1.46]',
                                'text-left',
                                'transition-colors',
                                item.variant === ContextMenuItemVariant.DESTRUCTIVE
                                    ? 'text-alert-error hover:bg-alert-error-bg'
                                    : 'text-midnight hover:bg-grey-outline-light',
                                item.disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
                            )}
                        >
                            {item.icon && (
                                <span className="shrink-0 size-4 flex items-center justify-center">{item.icon}</span>
                            )}
                            <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">{item.label}</span>
                        </button>
                    ))}
                </div>
            </div>,
            document.body,
        );

    // For single element children, avoid wrapper divs to preserve DOM structure (e.g., table rows)
    if (children) {
        const childArray = Children.toArray(children);
        if (childArray.length === 1 && isValidElement(childArray[0])) {
            const child = childArray[0] as ReactElement<{ onContextMenu?: React.MouseEventHandler }>;
            return (
                <>
                    {cloneElement(child, { onContextMenu: handleContextMenu })}
                    {renderPanel()}
                </>
            );
        }
    }

    return (
        <div className={cn('relative', children ? '' : 'inline-block', className)} ref={containerRef} {...props}>
            {renderTrigger()}
            {renderPanel()}
        </div>
    );
};

ContextMenu.displayName = 'ContextMenu';
